import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getStoredToken, clearUserSession } from "../utils/auth";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const OTP_RESEND_SECONDS = 60;
const REASON_MAX_LENGTH = 500;
const REDIRECT_DELAY_MS = 1500;

const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-200 outline-none";

const postToApi = async (path, body) => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", token: getStoredToken() },
    body: JSON.stringify(body || {}),
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok && data.success, status: response.status, data };
};

export default function DeleteAccountModal({ isSocialLogin, onClose }) {
  const [step, setStep] = useState("warning");
  const [understood, setUnderstood] = useState(false);
  const [useOtp, setUseOtp] = useState(Boolean(isSocialLogin));
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpNotice, setOtpNotice] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [reason, setReason] = useState("");
  const [error, setError] = useState({ message: "", blocked: false });
  const [sendingOtp, setSendingOtp] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const switchMethod = () => {
    setUseOtp(!useOtp);
    setError({ message: "", blocked: false });
  };

  const handleSendOtp = async () => {
    setSendingOtp(true);
    setError({ message: "", blocked: false });
    try {
      const { ok, data } = await postToApi("/user/delete-account/otp");
      if (ok) {
        setOtpNotice(data.result?.message || "OTP sent");
        setResendIn(OTP_RESEND_SECONDS);
      } else {
        setError({ message: data.message || "Could not send the OTP. Please try again.", blocked: false });
      }
    } catch (err) {
      setError({ message: "Could not send the OTP. Please try again.", blocked: false });
    }
    setSendingOtp(false);
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    if (useOtp ? !/^\d{6}$/.test(otp) : !password) {
      setError({ message: useOtp ? "Please enter the 6-digit OTP." : "Please enter your password.", blocked: false });
      return;
    }

    setDeleting(true);
    setError({ message: "", blocked: false });
    try {
      const { ok, status, data } = await postToApi("/user/delete-account", {
        ...(useOtp ? { otp } : { password }),
        ...(reason.trim() && { reason: reason.trim() }),
      });
      if (ok) {
        toast.success(data.result?.message || "Your account has been deleted");
        clearUserSession();
        // Full reload (as on logout) once the toast has been seen, so no in-memory user state survives
        setTimeout(() => window.location.replace("/login"), REDIRECT_DELAY_MS);
        return;
      }
      setError({ message: data.message || "Could not delete your account. Please try again.", blocked: status === 409 });
    } catch (err) {
      setError({ message: "Could not delete your account. Please try again.", blocked: false });
    }
    setDeleting(false);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4" onClick={deleting ? undefined : onClose}>
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="absolute top-3 right-3 text-[#fff] bg-[#095228] rounded-full p-1 disabled:opacity-50"
          onClick={onClose}
          disabled={deleting}
        >
          ✖
        </button>
        <h2 className="text-base font-medium text-center mt-2 mb-4 px-8">Delete Account</h2>

        {step === "warning" ? (
          <>
            <ul className="list-disc space-y-2 pl-5 text-sm text-gray-700">
              <li>Your profile, posts, stories, followers, groups and chats will be removed permanently.</li>
              <li>Your orders and payment records are kept.</li>
              <li>You can't delete your account while your wallet has a balance or an order is still in progress.</li>
              <li>You can sign up again later with the same email or phone number, as a new account.</li>
            </ul>
            <label className="mt-5 flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" className="mt-1" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
              I understand that deleting my account can't be undone.
            </label>
            <button
              type="button"
              disabled={!understood}
              onClick={() => setStep("confirm")}
              className="mt-5 w-full rounded-lg bg-red-600 px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Continue
            </button>
          </>
        ) : (
          <form onSubmit={handleDelete} className="space-y-4">
            {useOtp ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="Enter 6-digit OTP"
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={sendingOtp || resendIn > 0}
                    className="shrink-0 rounded-lg bg-[#095228] px-3 py-2 text-sm text-white disabled:opacity-50"
                  >
                    {sendingOtp ? "Sending..." : resendIn > 0 ? `Resend in ${resendIn}s` : otpNotice ? "Resend OTP" : "Send OTP"}
                  </button>
                </div>
                {otpNotice && <p className="text-xs text-gray-500">{otpNotice}</p>}
              </div>
            ) : (
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className={inputClass}
              />
            )}
            <button type="button" onClick={switchMethod} className="text-sm text-[#095228] underline">
              {useOtp ? "Use my password instead" : "Use an OTP instead"}
            </button>

            <div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={REASON_MAX_LENGTH}
                rows={3}
                placeholder="Why are you leaving? (optional)"
                className={`${inputClass} resize-none`}
              />
              <p className="text-right text-xs text-gray-400">{reason.length}/{REASON_MAX_LENGTH}</p>
            </div>

            {error.message && (
              <div className="text-sm text-red-500">
                <p>{error.message}</p>
                {error.blocked && (
                  <p className="mt-1 space-x-3">
                    <Link to="/wallet" className="underline">Go to Wallet</Link>
                    <Link to="/myorder" className="underline">My Orders</Link>
                  </p>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={deleting}
              className="w-full rounded-lg bg-red-600 px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {deleting ? "Deleting..." : "Delete my account"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
